import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import Menu from 'classes/Menu/Search/Menu';
import MenuList from 'classes/Menu/Search/MenuList';
import Search from 'classes/Menu/Search/Search';
import MenuAdvancedSearch from 'components/desktop/Menu/AdvancedSearch/AdvancedSearch';
import SearchResultListComponent from 'components/desktop/Menu/Search/SearchResultList';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import { MenuSortOrder } from 'enums/SortOrder/MenuSortOrder';
import clone from 'functions/clone';
import delay from 'functions/delay';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { usePermissions } from 'hooks/usePermissions';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 10;

const MenuSearchComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams();
	const { isMenuEditor } = usePermissions();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxCurrency = useSelector((state: RootState) => state.currency);
	const reduxPreferredNutrientIds = useSelector((state: RootState) => state.preferredNutrientIds);

	const [isLoading, setIsLoading] = useState(false);
	const [renderRanges, setRenderRanges] = useState<boolean>(false);

	const [search, setSearch] = useState<Search>(new Search());
	const [searchIsLoading, setSearchIsLoading] = useState(false);
	const [menuList, setMenuList] = useState<MenuList>(new MenuList());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (window.location.href.includes('?')) {
			setSearchIsLoading(true);
			setIsLoading(true);
			await search.initialize();
			if (props.archive !== undefined && props.archive == true) {
				search.showOnlyDeleted = true;
			}
			openAdvancedSearch();
			setSearchIsLoading(false);
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getMenues();
			setIsLoading(false);
		} else {
			setSearchIsLoading(true);
			await search.initialize();
			if (props.archive !== undefined && props.archive == true) {
				search.showOnlyDeleted = true;
			}
			await getMenues();
			setSearchIsLoading(false);
		}
		setSearch(clone(search));
	};

	const handleRestoreMenu = async (menu: Menu): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('menu:RESTORE_TITLE'),
			text: t('menu:RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await menu.callApiRestore();
				await delay(3000);
				await Swal.fire({
					title: t('menu:RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getMenues();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const handleArchiveMenu = async (menu: Menu): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('menu:ARCHIVE_TITLE'),
			text: t('menu:ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await menu.callApiDelete();
				await delay(3000);
				await Swal.fire({
					title: t('menu:ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getMenues();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getMenues();
	};

	const handleChangeAdvancedSearchUrl = () => {
		search.pageIndex.value = 0;
		handleChangeUrl();
	};

	const openAdvancedSearch = () => {
		const advancedSearchButton = document.getElementById('advancedSearchButton');
		if (advancedSearchButton) advancedSearchButton.click();
	};

	const getMenues = async (): Promise<void> => {
		setIsLoading(true);
		const response = await menuList.callApi(
			search.mapToRequest(reduxCurrency, reduxPreferredNutrientIds)
		);
		response.do((x) => {
			menuList.mapFromApi(x);
			setMenuList(clone(menuList));
			search.mapFromApi(x);
		});
		setIsLoading(false);
	};

	const handleReset = () => {
		search.reset();
		setSearchTerm('');
		handleChangeUrl();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangeSortOrder = (value: string) => {
		search.sortOrder.value = value as MenuSortOrder;
		handleChangeUrl();
	};

	const handleChangePageSize = (value: string): void => {
		search.pageSize.value = Number(value);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const getSuggestedMenus = async (): Promise<ISelectItem[]> => {
		await menuList.search(searchTerm, props.archive ?? false);
		setMenuList(clone(menuList));
		return menuList.filtered;
	};

	const advancedSearchContainer = document.getElementById('advancedSearch');
	if (advancedSearchContainer) {
		advancedSearchContainer.addEventListener('shown.bs.collapse', function () {
			setRenderRanges(true);
		});
		advancedSearchContainer.addEventListener('hidden.bs.collapse', function () {
			setRenderRanges(false);
		});
	}

	const renderTitle = (): JSX.Element => {
		if (!props.archive) {
			if (params.menuPlanId) {
				return <h1>{t('menuPlan:ADD_MENU')}</h1>;
			} else {
				return <h1>{t('menu:SEARCH_MENU')}</h1>;
			}
		} else {
			return <h1>{t('menu:MENU_ARCHIVE')}</h1>;
		}
	};

	const handleCreateNewMenu = async () => {
		navigate('/menu/new');
	};
	return (
		<main className="container">
			<div className="d-flex justify-content-between">
				{renderTitle()}
				<RenderIf condition={isMenuEditor && !params.menuPlanId && !props.archive}>
					<BtnNew onClick={handleCreateNewMenu} name={t('menu:CREATE_MENU')} />
				</RenderIf>
			</div>
			<br />
			<div className="row">
				<div className="col-lg-7">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedMenus}
							handleSearch={handleSearch}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-2">
					<button
						id="advancedSearchButton"
						className="btn btn-primary"
						type="button"
						onClick={() => setRenderRanges(true)}
						data-bs-toggle="collapse"
						data-bs-target="#advancedSearch"
						aria-expanded="true"
						aria-controls="advancedSearch"
						disabled={searchIsLoading}
					>
						{searchIsLoading ? <Spinner animation="border" size="sm" /> : ''}
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={handleChangePageSize}
							currentValue={String(search.getPageSize())}
						/>
						<DropdownSortOrder
							type={'menu'}
							listFunction={handleChangeSortOrder}
							currentValue={search.getSortOrder()}
						/>
					</div>
				</div>
			</div>
			<div className={'collapse'} id="advancedSearch">
				<div className="card card-body mb-3">
					<MenuAdvancedSearch
						disabled={menuList.totalCount === 0}
						advancedSearch={search}
						setAdvancedSearch={setSearch}
						handleChangeUrl={handleChangeAdvancedSearchUrl}
						handleReset={handleReset}
						renderRanges={renderRanges}
					/>
				</div>
			</div>

			<LoadingAnimation isLoading={isLoading} />
			<p>{`${menuList.totalCount} ${t('_general:MENUES')}`}</p>
			<div className="mb-3">
				<SearchResultListComponent
					archive={props.archive ?? false}
					handleArchiveMenu={handleArchiveMenu}
					handleRestoreMenu={handleRestoreMenu}
					menuList={menuList}
				/>
			</div>
			<Pagination
				countOfAllListItems={menuList.totalCount}
				pageIndex={search.getPageIndex()}
				setPageIndex={handleChangePageIndex}
				listItemAmount={search.getPageSize()}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default MenuSearchComponent;
